import moment, { type Moment } from 'moment'
import * as R from 'ramda'
import {
  type BaseUser,
  type Constant,
  type NamedEntity,
  Utils,
} from '@pbt/pbt-ui-components'

import type {
  InventoryOrder,
  InventoryOrderItem,
  InventoryOrderItemReceipt,
} from '~/api/graphql/generated/types'
import { InventoryOrderReceiptReason } from '~/constants/inventoryOrders'
import type {
  InventoryOrderForTable,
  InventoryOrderItemForTable,
} from '~/types'
import { getInventoryItemDisplayName } from '~/utils/inventoryItemUtils'

import type { ReceiptFieldGroup } from '../order-item-receipts/hooks/useReceiptFields'
import type { Quantities } from '../order-item-receipts/OrderItemReceiptConfirmModal'

const STATUS_RECEIVED = 'RECEIVED'
const STATUS_CANCELLED = 'CANCELLED'
const STATUS_CLOSED = 'CLOSED'

const getLastReceipt = (items: InventoryOrderItem[]) => {
  const receiptsWithDates = items.flatMap((item) =>
    item.receipts
      .filter(({ creationDate }) => Boolean(creationDate))
      .map((receipt) => ({ date: moment(receipt.creationDate), receipt })),
  )

  if (!receiptsWithDates.length) {
    return null
  }

  return receiptsWithDates.reduce(
    (latest, current) => (current.date.isAfter(latest.date) ? current : latest),
    receiptsWithDates[0],
  )
}

const getLastReceivedBy = (items: InventoryOrderItem[]): string | null => {
  const lastReceipt = getLastReceipt(items)
  const name = Utils.getPersonString(lastReceipt?.receipt.createdBy as BaseUser)
  return name || null
}

const getDerivedInventoryOrderValues = (items: InventoryOrderItem[]) => {
  const categories = new Set<string>()
  const inventoryOrderFlags = { controlled: false, storeCold: false }
  const receivedDates: Moment[] = []
  let linesReceived = 0

  items.forEach((item) => {
    inventoryOrderFlags.storeCold ||=
      item.inventoryItem.variation?.storeCold ?? false
    inventoryOrderFlags.controlled ||= item.inventoryItem.controlled

    categories.add(item.inventoryItem.category.id)

    if (item.status.name.toUpperCase() === STATUS_RECEIVED) {
      linesReceived++
    }

    receivedDates.push(
      ...item.receipts
        .filter(({ creationDate }) => Boolean(creationDate))
        .map(({ creationDate }) => moment(creationDate)),
    )
  })

  return {
    categories: Array.from(categories),
    inventoryOrderFlags,
    lastReceivedDate: receivedDates.length
      ? moment.max(receivedDates).toISOString()
      : '',
    lastReceivedBy: getLastReceivedBy(items),
    linesReceived,
  }
}

export const transformInventoryOrderForTable = (
  order: InventoryOrder,
): InventoryOrderForTable => ({
  id: order.id,
  orderNumber: order.orderNumber,
  statusId: order.status.id,
  distributorId: order.distributor.id,
  distributorName: order.distributor.name,
  orderedByEmail: order.externalCreatorEmail || '',
  orderedByFirstName: order.externalCreatorFirstName || '',
  orderedByLastName: order.externalCreatorLastName || '',
  orderedDate: order.orderedDate,
  ...getDerivedInventoryOrderValues(order.items),
})

const getReceivedQuantities = (receipts: InventoryOrderItemReceipt[]) =>
  receipts.reduce(
    (acc, receipt) => {
      if (receipt.damageReason) {
        acc.damagedQuantity += receipt.receivedQuantity
      } else {
        acc.receivedQuantity += receipt.receivedQuantity
      }
      return acc
    },
    { receivedQuantity: 0, damagedQuantity: 0 },
  )

export const transformInventoryOrderItemForTable = (
  item: InventoryOrderItem,
): InventoryOrderItemForTable => ({
  id: item.id,
  cumulusId: item.inventoryItem.variation?.cumulusId || '',
  name: getInventoryItemDisplayName(item.inventoryItem),
  notes: item.notes || '',
  orderedQuantity: item.orderedQuantity,
  packageTypeId: item.packageType.id,
  packageUnitsId: item.packageUnits.id,
  perPackageAmount: item.perPackageAmount,
  sku: item.sku || '',
  statusId: item.status.id,
  storeCold: item.inventoryItem.variation?.storeCold ?? false,
  controlled: item.inventoryItem.controlled,
  receipts: item.receipts,
  ...getReceivedQuantities(item.receipts),
})

export const getReceivedBy = (inventoryOrder: InventoryOrder): string | null =>
  getLastReceivedBy(inventoryOrder.items)

/**
 * Checks if receiving is disabled for an order item.
 * Receiving is disabled when:
 * - The order status is "Closed" or "Cancelled", OR
 * - The order line item status is "Cancelled"
 */
export const isReceivingDisabled = (
  orderStatus: NamedEntity,
  itemStatus: NamedEntity,
): boolean => {
  const orderStatusUpper = orderStatus.name.toUpperCase()
  const itemStatusUpper = itemStatus.name.toUpperCase()

  return (
    orderStatusUpper === STATUS_CLOSED ||
    orderStatusUpper === STATUS_CANCELLED ||
    itemStatusUpper === STATUS_CANCELLED
  )
}

export const getReceiptQuantitiesByReason = (
  receipts: ReceiptFieldGroup[],
  orderedQuantity: number,
  receiveReasons: Constant[],
) => {
  const quantities: Quantities = {
    receiving: 0,
    receivingDamaged: 0,
    receivingOverage: 0,
    rejectingDamaged: 0,
    rejectingExpired: 0,
    rejectingRecalled: 0,
    rejectingOther: 0,
    rejectingOverage: 0,
    remaining: 0,
  }

  const reasonMap: Record<string, keyof Quantities> = {
    [InventoryOrderReceiptReason.DAMAGED]: 'rejectingDamaged',
    [InventoryOrderReceiptReason.EXPIRY_SHORT_DATED]: 'rejectingExpired',
    [InventoryOrderReceiptReason.RECALLED]: 'rejectingRecalled',
    [InventoryOrderReceiptReason.OVERAGE]: 'rejectingOverage',
    [InventoryOrderReceiptReason.OTHER]: 'rejectingOther',
  }

  receipts.forEach(({ fields }) => {
    const { value: qty = 0 } = fields.receivedQuantity
    const { value: receiveReasonId } = fields.receiveReasonId
    const { value: rejected } = fields.rejected

    if (!receiveReasonId) {
      quantities.receiving += qty
      return
    }

    if (!rejected) {
      quantities.receivingDamaged += qty
      return
    }

    const reasonName =
      receiveReasons.find((r) => r.id === receiveReasonId)?.name || ''
    const reasonKey = reasonMap[reasonName] || 'rejectingOther'
    quantities[reasonKey] += qty
  })

  const overage = Math.max(
    0,
    quantities.receiving + quantities.receivingDamaged - orderedQuantity,
  )
  quantities.receivingOverage = overage

  // Deduct overage from receiving first (up to its value)
  const overageFromReceiving = Math.min(overage, quantities.receiving)
  quantities.receiving -= overageFromReceiving

  // Deduct remaining overage from receivingDamaged
  const remainingOverage = overage - overageFromReceiving
  quantities.receivingDamaged = Math.max(
    0,
    quantities.receivingDamaged - remainingOverage,
  )

  const totalProcessed = R.pipe(R.values, R.sum)(quantities)
  quantities.remaining = Math.max(0, orderedQuantity - totalProcessed)

  return quantities
}
